import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import type { TireEfficiencyFlag } from '../../types';
import { TireEfficiencyFlagItem } from '../../atoms/TireEfficiencyFlagItem/TireEfficiencyFlagItem.client';
import { DataSheets } from '../../atoms/DataSheets/DataSheets';

/**
 * Determines whether tire efficiency flags cards should be rendered.
 *
 * @param {Object} params - The parameters object
 * @param {TireEfficiencyFlag} params.efficiencyFlags - The tire efficiency flags to check
 * @returns {boolean} True if there are tire efficiency flags to display, false otherwise
 */
export const checkRenderEfficiencyFlagsCards = (
  efficiencyFlags: TireEfficiencyFlagsCardProps['efficiencyFlags'],
): efficiencyFlags is NonNullable<TireEfficiencyFlagsCardProps['efficiencyFlags']> =>
  efficiencyFlags && efficiencyFlags.flags.length > 0;

/**
 * Props for rendering the tire efficiency flags card.
 *
 * `efficiencyFlags` contains the label image link and the list of tire flag entries.
 * `dataSheets` can optionally render additional datasheet download links below the flags.
 */
export type TireEfficiencyFlagsCardProps = {
  efficiencyFlags: TireEfficiencyFlag;
  dataSheets?: string[];
  /** Optional product name, named in the datasheet links for screen readers. */
  productName?: string;
};

/**
 * Renders tire efficiency flags inside a card and optionally appends datasheet links.
 *
 * Use `checkRenderEfficiencyFlagsCards` to check if any tire efficiency data is available before rendering this component.
 *
 * @example
 * ```tsx
 * checkRenderEfficiencyFlagsCards({ efficiencyFlags }) && (
 *   <TireEfficiencyFlagsCard
 *     efficiencyFlags={efficiencyFlags}
 *     dataSheets={dataSheets}
 *   />
 * )
 * ```
 */
export const TireEfficiencyFlagsCard = ({
  efficiencyFlags,
  dataSheets,
  productName,
}: TireEfficiencyFlagsCardProps) => (
  <Card layout="outlined" hasAutoSeparation>
    <CardContent className="pb-2">
      <div className="flex flex-row flex-wrap gap-1.5">
        {efficiencyFlags.flags.map((flag) => (
          <TireEfficiencyFlagItem
            key={`${flag.type}-${flag.level}`}
            text={flag.level}
            type={flag.type}
            linkToEnergieEfficiencyLabel={efficiencyFlags.linkToEnergieEfficiencyLabel}
            size="byBreakpoint"
          />
        ))}
      </div>
    </CardContent>

    {dataSheets && dataSheets.length > 0 && (
      <CardContent className="pb-2">
        <DataSheets dataSheetUrls={dataSheets} productName={productName} />
      </CardContent>
    )}
  </Card>
);
